import { useEffect, useLayoutEffect, useRef } from 'react'

import {
  Alert,
  ArrowRightIcon as ArrowForwardIcon,
  FileTextIcon as DescriptionIcon,
  LayoutGridIcon as GridViewIcon,
  Icon,
  IconButton,
  Tooltip,
  TriangleAlertIcon as WarningIcon,
  Button,
  Divider,
  Link,
  NavigationGroup,
  Spinner,
  Text,
  TextField,
} from 'src/design-system/ui'

import { routes } from 'src/platform/router'

import { useApp2Cases } from './App2CasesContext'
import { App2FleetTable } from './App2FleetTable'

export const App2CasesPanel = ({
  active = true,
  compact = false,
  headerAction,
  onNavigate: afterNavigate,
}: {
  active?: boolean
  compact?: boolean
  headerAction?: React.ReactNode
  onNavigate?: () => void
}) => {
  const {
    applicationId,
    applicationNumber,
    setApplicationNumber,
    invalidApplicationNumber,
    setInvalidApplicationNumber,
    recentIds,
    recentExpanded,
    setRecentExpanded,
    otherExpanded,
    setOtherExpanded,
    otherRequested,
    requestWorklist,
    showOlder,
    setShowOlder,
    scrollTop,
    data,
    loading,
    error,
    refetch,
    bands,
    navigateTo,
  } = useApp2Cases()
  const visibleRecentIds =
    !compact || recentExpanded ? recentIds : recentIds.slice(0, 5)
  const groups = [
    { label: 'Issues', items: bands.people },
    { label: 'Other cases', items: bands.pipes },
    ...(bands.older.length
      ? [{ label: 'Older cases', items: bands.older }]
      : []),
  ]
  const selectedRef = useRef<HTMLAnchorElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const previousApplicationId = useRef(applicationId)

  useEffect(() => {
    if (active) requestWorklist()
  }, [active, requestWorklist])

  useLayoutEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollTop.current
  }, [scrollTop])
  useEffect(() => {
    // Keep the scroll position on a fleet/sidebar transition. Within the
    // sidebar, bring a newly selected case into view when needed.
    if (compact && previousApplicationId.current !== applicationId) {
      selectedRef.current?.scrollIntoView?.({ block: 'nearest' })
    }
    previousApplicationId.current = applicationId
  }, [applicationId, compact])

  const open = (href: string) => {
    afterNavigate?.()
    navigateTo(href)
  }
  const onNavigate = (event: React.MouseEvent<Element>, href: string) => {
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    )
      return
    event.preventDefault()
    open(href)
  }
  const openApplication = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const value = applicationNumber.trim().replace(/^#/, '')
    const id = Number(value)
    // Positive GraphQL Ints only; reject partial numbers before routing.
    if (
      !/^\d+$/.test(value) ||
      !Number.isSafeInteger(id) ||
      id <= 0 ||
      id > 2_147_483_647
    ) {
      setInvalidApplicationNumber(true)
      return
    }
    setInvalidApplicationNumber(false)
    setApplicationNumber('')
    open(routes.app2({ applicationId: id }))
  }
  return (
    <nav
      id="app2-case-list"
      aria-label="Evie case list"
      className={`app2-cases${compact ? ' app2-cases--compact' : ''}`}
    >
      <header className="app2-cases-header">
        <div className="ds:min-w-0 ds:flex-1">
          <Text
            as={compact ? 'h2' : 'h1'}
            variant={compact ? 'body' : 'heading'}
            className="app2-cases-title"
          >
            Cases
          </Text>
          {!compact && (
            <Text tone="muted">Review issues and keep track of the fleet.</Text>
          )}
        </div>
        {headerAction}
      </header>
      {compact && <Divider />}
      <form
        aria-label="Navigate to application"
        noValidate
        onSubmit={openApplication}
        className="ds:flex ds:items-end ds:gap-2 ds:p-4 ds:shrink-0"
      >
        <TextField
          label="Application number"
          trailingAction={
            <Tooltip content="Open application">
              <IconButton
                size="compact"
                type="submit"
                aria-label="Open application"
                className="app2-small-icon-button"
              >
                <Icon icon={ArrowForwardIcon} size="sm" tone="muted" />
              </IconButton>
            </Tooltip>
          }
          value={applicationNumber}
          className="ds:min-w-0 ds:flex-1"
          inputProps={{ inputMode: 'numeric', placeholder: 'e.g. 1185281' }}
          onChange={(value) => {
            setApplicationNumber(value)
            setInvalidApplicationNumber(false)
          }}
          errorMessage={
            invalidApplicationNumber
              ? 'Enter a valid application number.'
              : undefined
          }
        />
      </form>
      {applicationId !== null && (
        <Link
          href={routes.app2Worklist()}
          onClick={(event) => onNavigate(event, routes.app2Worklist())}
          className="app2-case-link"
        >
          <Icon icon={GridViewIcon} size="sm" tone="muted" />
          Fleet view
        </Link>
      )}
      <div
        ref={scrollRef}
        data-testid="app2-cases-scroll"
        className="app2-cases-scroll"
        onScroll={(event) => {
          scrollTop.current = event.currentTarget.scrollTop
        }}
      >
        {recentIds.length > 0 && (
          <NavigationGroup
            label="Recent"
            headingLevel={compact ? 3 : 2}
            tone={compact ? 'muted' : 'default'}
          >
            <ul
              id="app2-recent-applications"
              aria-label="Recent applications"
              className={`app2-cases-list${compact ? '' : ' app2-cases-list--inline'}`}
            >
              {visibleRecentIds.map((id) => {
                const href = routes.app2({ applicationId: id })
                const selected = id === applicationId
                return (
                  <li key={id}>
                    <Link
                      href={href}
                      onClick={(event) => onNavigate(event, href)}
                      aria-current={selected ? 'page' : undefined}
                      ref={selected ? selectedRef : undefined}
                      className="app2-case-link"
                    >
                      #{id}
                    </Link>
                  </li>
                )
              })}
            </ul>
            {compact && recentIds.length > 5 && (
              <Button
                variant="quiet"
                aria-expanded={recentExpanded}
                aria-controls="app2-recent-applications"
                onPress={() => setRecentExpanded((expanded) => !expanded)}
                className="ds:m-2"
              >
                {recentExpanded
                  ? 'Show less'
                  : `Show more (${recentIds.length - 5})`}
              </Button>
            )}
            <Divider />
          </NavigationGroup>
        )}
        {loading && (
          <div className="ds:flex ds:justify-center ds:p-4">
            <Spinner aria-label="Loading case list" />
          </div>
        )}
        {error && (
          <Alert tone="warning">
            Case list unavailable.
            <Button variant="quiet" onPress={() => void refetch()}>
              Retry
            </Button>
          </Alert>
        )}
        {data &&
          groups.map(({ label, items }) => (
            <NavigationGroup
              key={label}
              label={
                label === 'Other cases' &&
                ((!otherRequested && !showOlder) || loading)
                  ? label
                  : `${label} (${items.length})`
              }
              headingLevel={compact ? 3 : 2}
              tone={compact ? 'muted' : 'default'}
              collapsible={label === 'Other cases'}
              isExpanded={otherExpanded}
              onExpandedChange={setOtherExpanded}
            >
              {label === 'Other cases' && !otherExpanded ? null : loading &&
                items.length === 0 ? null : items.length === 0 ? (
                <Text variant="micro" tone="muted" className="ds:px-4 ds:py-2">
                  {label === 'Issues'
                    ? 'No open issues right now.'
                    : 'No cases in this group.'}
                </Text>
              ) : !compact ? (
                <App2FleetTable
                  rows={items}
                  label={label}
                  onOpen={open}
                  onNavigate={onNavigate}
                />
              ) : (
                <ul className="app2-cases-list">
                  {items.map((item) => {
                    const href = routes.app2({
                      applicationId: item.applicationId,
                    })
                    const selected = item.applicationId === applicationId
                    return (
                      <li key={item.applicationId}>
                        <Link
                          href={href}
                          onClick={(event) => onNavigate(event, href)}
                          aria-current={selected ? 'page' : undefined}
                          ref={
                            selected &&
                            !visibleRecentIds.includes(applicationId)
                              ? selectedRef
                              : undefined
                          }
                          className="app2-case-link"
                        >
                          <Icon
                            icon={
                              item.topFindingSeverity
                                ? WarningIcon
                                : DescriptionIcon
                            }
                            size="sm"
                            tone={
                              item.topFindingSeverity === 'critical'
                                ? 'error'
                                : item.topFindingSeverity
                                  ? 'warning'
                                  : 'muted'
                            }
                          />
                          <span className="ds:min-w-0 ds:flex-1">
                            <Text as="span">#{item.applicationId}</Text>{' '}
                            <Text
                              as="span"
                              variant="micro"
                              tone="muted"
                              className="ds:block ds:truncate"
                            >
                              {item.topFindingLabel ??
                                item.twoDotsStatus ??
                                'No actionable findings'}
                            </Text>
                          </span>
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              )}
            </NavigationGroup>
          ))}
        {!showOlder &&
          data &&
          (!otherRequested || !!data.app2Worklist.totalBeyondCutline) && (
            <Button
              variant="quiet"
              onPress={() => setShowOlder(true)}
              className="ds:m-2"
            >
              Show older
              {otherRequested && !loading
                ? ` (${data.app2Worklist.totalBeyondCutline})`
                : ''}
            </Button>
          )}
      </div>
    </nav>
  )
}
